import type { Map as LeafletMap } from "leaflet";
import L from "leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCompressAlt,
  FaCrosshairs,
  FaExpandAlt,
  FaInfoCircle,
  FaLayerGroup,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { MapContainer, TileLayer, ZoomControl, useMap } from "react-leaflet";
import {
  ALGERIA_BOUNDARY,
  ALGERIA_CENTER,
  displayValue,
  formatNumber,
  getBadgeClass,
  getCapacityStatus,
  getSectorColor,
} from "./cartographyUtils";
import { CartographySupplier } from "./types";

import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet/dist/leaflet.css";

interface Props {
  suppliers: CartographySupplier[];
  sectors: string[];
}

type BaseMapKey = "plan" | "satellite" | "topo" | "dark";

const ALGERIA_GEOJSON_URL =
  "https://raw.githubusercontent.com/fr33dz/Algeria-geojson/master/dza.geojson";

const ALGERIA_NORTH_CENTER: [number, number] = [36.55, 3.10];
const ALGERIA_NORTH_ZOOM = 8;

const BASE_MAPS: Record<
  BaseMapKey,
  {
    label: string;
    url: string;
    attribution: string;
  }
> = {
  plan: {
    label: "Plan",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap contributors",
  },
  satellite: {
    label: "Satellite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
  },
  topo: {
    label: "Topo",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenTopoMap contributors",
  },
  dark: {
    label: "Dark",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
  },
};

function createSectorIcon(sector: string, hasAlert: boolean) {
  const color = hasAlert ? "#dc2626" : getSectorColor(sector);

  return L.divIcon({
    className: "aida-div-marker",
    html: `
      <div class="aida-marker-wrap" style="--aida-marker-color:${color};">
        <span class="aida-marker-pulse"></span>
        <span class="aida-marker-core"></span>
      </div>
    `,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

function createClusterIcon(cluster: any) {
  const count = cluster.getChildCount();

  return L.divIcon({
    className: "aida-cluster-wrapper",
    html: `
      <div class="aida-cluster-marker">
        <span>${count}</span>
      </div>
    `,
    iconSize: L.point(46, 46, true),
  });
}

function escapeHtml(value: any) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getPopupHtml(supplier: CartographySupplier) {
  const hasAlert = supplier.nonCapacitaryProducts > 0;
  const capacityStatus = hasAlert ? "Non capacitaire" : "Capacitaire";
  const topProducts = supplier.products.slice(0, 4);

  const productsHtml =
    topProducts.length > 0
      ? `
        <div class="aida-popup-products">
          <strong>Produits / pièces suivis</strong>
          ${topProducts
        .map((product) => {
          const productStatus = getCapacityStatus(product);

          return `
                <div class="aida-popup-product-line">
                  <span>${escapeHtml(product.product_name)}</span>
                  <em class="${getBadgeClass(productStatus)}">
                    ${escapeHtml(productStatus)}
                  </em>
                </div>
              `;
        })
        .join("")}
        </div>
      `
      : "";

  return `
    <div class="aida-popup-card aida-popup-card-exportable">
      <div class="aida-popup-header">
        <div class="aida-popup-logo">
          <span>🏭</span>
        </div>

        <div>
          <div class="aida-popup-title">
            ${escapeHtml(supplier.legal_name)}
          </div>

          <div class="aida-popup-subtitle">
            📍 ${escapeHtml(displayValue(supplier.wilaya))} • ${escapeHtml(
    displayValue(supplier.mainSector),
  )}
          </div>
        </div>
      </div>

      <div class="aida-popup-badges">
        <span class="${getBadgeClass(capacityStatus)}">
          ${escapeHtml(capacityStatus)}
        </span>

        <span class="${getBadgeClass(supplier.export_potential)}">
          ${escapeHtml(displayValue(supplier.export_potential))}
        </span>

        <span class="${getBadgeClass(supplier.homologation_status)}">
          ${escapeHtml(displayValue(supplier.homologation_status))}
        </span>

        <span class="${getBadgeClass(supplier.pyramid_level)}">
          ${escapeHtml(displayValue(supplier.pyramid_level))}
        </span>
      </div>

      <div class="aida-popup-grid aida-popup-grid-exportable">
        <div>
          <span>Produits</span>
          <strong>${supplier.products.length}</strong>
        </div>

        <div>
          <span>Capacité déclarée</span>
          <strong>${escapeHtml(formatNumber(supplier.totalCapacity))}</strong>
        </div>

        <div>
          <span>Besoin constructeur</span>
          <strong>${escapeHtml(formatNumber(supplier.requestedCapacity))}</strong>
        </div>

        <div>
          <span>Taux couverture</span>
          <strong>${supplier.coverageRate !== null ? `${supplier.coverageRate}%` : "—"
    }</strong>
        </div>

        <div>
          <span>Export</span>
          <strong>${supplier.exportableProducts}</strong>
        </div>

        <div>
          <span>Substitution import</span>
          <strong>${supplier.substitutionProducts}</strong>
        </div>
      </div>

      <div class="aida-popup-progress">
        <div class="aida-popup-progress-head">
          <span>Couverture capacitaire</span>
          <strong>${supplier.coverageRate !== null
      ? `${supplier.coverageRate}%`
      : "Non renseigné"
    }</strong>
        </div>

        <div class="aida-popup-progress-bar">
          <span style="width:${Math.min(supplier.coverageRate || 0, 100)}%"></span>
        </div>
      </div>

      ${productsHtml}

      <div class="aida-popup-decision-box">
        <strong>Lecture décisionnelle</strong>
        <span>
          ${hasAlert
      ? `${supplier.nonCapacitaryProducts} produit(s) nécessitent un renforcement de capacité ou un plan d’investissement.`
      : "Fournisseur à capacité conforme sur les produits renseignés."
    }
        </span>
      </div>
    </div>
  `;
}

const AlgeriaBoundaryLayer: React.FC<{
  algeriaGeoJson: any;
  onBoundsReady?: (bounds: L.LatLngBounds) => void;
}> = ({ algeriaGeoJson, onBoundsReady }) => {
  const map = useMap();

  useEffect(() => {
    let layer: L.GeoJSON | L.Polygon | null = null;

    if (algeriaGeoJson) {
      layer = L.geoJSON(algeriaGeoJson, {
        style: () =>
          ({
            color: "#006233",
            weight: 2,
            fillColor: "#006233",
            fillOpacity: 0.045,
            opacity: 0.8,

            // Important : évite l'erreur Maximum call stack size exceeded
            // sur les gros GeoJSON GADM / frontières détaillées.
            smoothFactor: 0,
            noClip: false,
          }) as any,
      });
    } else {
      layer = L.polygon(ALGERIA_BOUNDARY, {
        color: "#006233",
        weight: 2,
        fillColor: "#006233",
        fillOpacity: 0.045,
        opacity: 0.8,
        smoothFactor: 0,
      });
    }

    layer.addTo(map);

    const bounds = layer.getBounds();

    if (bounds.isValid()) {
      map.setMaxBounds(bounds.pad(0.35));
      onBoundsReady?.(bounds);
    }

    return () => {
      layer?.removeFrom(map);
    };
  }, [map, algeriaGeoJson, onBoundsReady]);

  return null;
};

const SupplierClusterLayer: React.FC<{
  suppliers: CartographySupplier[];
}> = ({ suppliers }) => {
  const map = useMap();

  useEffect(() => {
    const clusterGroup = (L as any).markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      maxClusterRadius: 45,
      iconCreateFunction: createClusterIcon,
    });

    const circleLayers: L.CircleMarker[] = [];

    suppliers.forEach((supplier) => {
      const hasAlert = supplier.nonCapacitaryProducts > 0;
      const color = hasAlert ? "#dc2626" : getSectorColor(supplier.mainSector);

      const circle = L.circleMarker([supplier.lat, supplier.lng], {
        radius: Math.min(26, Math.max(9, supplier.products.length * 3 + 7)),
        color,
        fillColor: color,
        fillOpacity: hasAlert ? 0.15 : 0.1,
        weight: 1,
      });

      circle.addTo(map);
      circleLayers.push(circle);

      const marker = L.marker([supplier.lat, supplier.lng], {
        icon: createSectorIcon(supplier.mainSector, hasAlert),
      });

      marker.bindPopup(getPopupHtml(supplier), {
        maxWidth: 380,
        minWidth: 340,
        className: "aida-map-popup",
        autoPan: true,
        autoPanPadding: L.point(50, 120),
        keepInView: true,
        closeButton: true,
      });
      marker.on("popupopen", () => {
        const mapShell = map.getContainer().closest(".aida-map-shell");
        mapShell?.classList.add("aida-popup-open");

        window.setTimeout(() => {
          const popup = marker.getPopup();
          const popupLatLng = popup?.getLatLng();

          if (popupLatLng) {
            map.panInside(popupLatLng, {
              paddingTopLeft: L.point(40, 130),
              paddingBottomRight: L.point(40, 80),
              animate: true,
            });
          } else {
            map.panTo(marker.getLatLng(), {
              animate: true,
              duration: 0.25,
            });
          }

          map.invalidateSize();
        }, 120);
      });

      marker.on("popupclose", () => {
        const mapShell = map.getContainer().closest(".aida-map-shell");
        mapShell?.classList.remove("aida-popup-open");
      });

      clusterGroup.addLayer(marker);
    });

    clusterGroup.addTo(map);

    return () => {
      circleLayers.forEach((circle) => circle.removeFrom(map));
      clusterGroup.removeFrom(map);
    };
  }, [map, suppliers]);

  return null;
};

const CartographyMap: React.FC<Props> = ({ suppliers, sectors }) => {
  const mapRef = useRef<LeafletMap | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const algeriaBoundsRef = useRef<L.LatLngBounds | null>(null);

  const [baseMap, setBaseMap] = useState<BaseMapKey>("plan");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [algeriaGeoJson, setAlgeriaGeoJson] = useState<any>(null);

  const visibleSectors = useMemo(() => sectors.slice(0, 10), [sectors]);

  const totalAlerts = useMemo(
    () =>
      suppliers.reduce(
        (sum, supplier) => sum + supplier.nonCapacitaryProducts,
        0,
      ),
    [suppliers],
  );

  const totalCapacity = useMemo(
    () => suppliers.reduce((sum, supplier) => sum + supplier.totalCapacity, 0),
    [suppliers],
  );

  const wilayaCount = useMemo(
    () =>
      new Set(suppliers.map((supplier) => supplier.wilaya).filter(Boolean))
        .size,
    [suppliers],
  );

  useEffect(() => {
    let mounted = true;

    fetch(ALGERIA_GEOJSON_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error("GeoJSON Algérie non disponible");
        }

        return response.json();
      })
      .then((data) => {
        if (mounted) {
          setAlgeriaGeoJson(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setAlgeriaGeoJson(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const active = document.fullscreenElement === shellRef.current;
      setIsFullscreen(active);

      window.setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 250);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    window.setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 250);
  }, [baseMap, isFullscreen]);

  const activeBaseMap = BASE_MAPS[baseMap];

  // const centerOnAlgeria = () => {
  //   if (!mapRef.current) return;

  //   if (algeriaBoundsRef.current?.isValid()) {
  //     mapRef.current.fitBounds(algeriaBoundsRef.current, {
  //       padding: [28, 28],
  //       maxZoom: 6,
  //     });
  //     return;
  //   }

  //   mapRef.current.setView(ALGERIA_CENTER, 5);
  // };

  const centerOnAlgeria = () => {
    if (!mapRef.current) return;

    mapRef.current.setView(ALGERIA_NORTH_CENTER, ALGERIA_NORTH_ZOOM, {
      animate: true,
    });
  };

  const fitToSuppliers = () => {
    if (!mapRef.current || suppliers.length === 0) return;

    const bounds = L.latLngBounds(suppliers.map((s) => [s.lat, s.lng]));
    mapRef.current.fitBounds(bounds, { padding: [46, 46], maxZoom: 8 });
  };

  const toggleFullscreen = async () => {
    if (!shellRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await shellRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      setIsFullscreen((value) => !value);
    }

    window.setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 250);
  };

  return (
    <div
      ref={shellRef}
      className={`aida-map-shell aida-map-shell-exportable ${isFullscreen ? "aida-map-shell-fullscreen" : ""
        }`}
    >
      <div className="aida-map-top-ribbon aida-map-top-ribbon-compact">
        <div>
          <span className="aida-map-ribbon-label">AIDA • Algérie</span>
          <strong>Cartographie industrielle automobile</strong>
        </div>

        <div className="aida-map-ribbon-stats">
          <span>{suppliers.length} fournisseurs</span>
          <span>{wilayaCount} wilayas</span>
          <span>{formatNumber(totalCapacity)} capacité</span>
          <span className={totalAlerts > 0 ? "is-alert" : ""}>
            {totalAlerts} alertes
          </span>
        </div>
      </div>

      <div className="aida-map-toolbar aida-map-toolbar-pro">
        <button
          type="button"
          onClick={centerOnAlgeria}
          title="Centrer sur le géofencing Algérie"
        >
          <FaCrosshairs />
        </button>

        <button
          type="button"
          onClick={fitToSuppliers}
          title="Afficher les fournisseurs"
        >
          <FaMapMarkerAlt />
        </button>

        <button
          type="button"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Réduire" : "Plein écran"}
        >
          {isFullscreen ? <FaCompressAlt /> : <FaExpandAlt />}
        </button>
      </div>

      <div className="aida-map-basemap-control">
        <FaLayerGroup />
        <select
          value={baseMap}
          onChange={(event) => setBaseMap(event.target.value as BaseMapKey)}
          title="Type de carte"
        >
          {Object.entries(BASE_MAPS).map(([key, item]) => (
            <option key={key} value={key}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="aida-map-legend aida-map-legend-exportable">
        <div className="aida-map-legend-title">
          <FaInfoCircle /> Légende industrielle
        </div>

        {visibleSectors.length === 0 ? (
          <span className="text-muted small">Aucune filière</span>
        ) : (
          visibleSectors.map((sector) => (
            <div className="aida-map-legend-item" key={sector}>
              <span style={{ background: getSectorColor(sector) }} />
              <strong>{sector}</strong>
            </div>
          ))
        )}

        <div className="aida-map-legend-divider" />

        <div className="aida-map-legend-item">
          <span style={{ background: "#dc2626" }} />
          <strong>Alerte capacité</strong>
        </div>

        <div className="aida-map-legend-note">
          Rouge = besoin constructeur supérieur à la capacité déclarée.
        </div>
      </div>

            {/* <MapContainer
        center={ALGERIA_CENTER}
        zoom={5}
        minZoom={4}
        maxZoom={18}
        zoomControl={false}
        scrollWheelZoom={false}
        maxBoundsViscosity={0.85}
        preferCanvas={true}
        className="aida-leaflet-map"
        ref={(map) => {
          if (map) mapRef.current = map;
        }}
      > */}

      <MapContainer
        center={ALGERIA_NORTH_CENTER}
        zoom={ALGERIA_NORTH_ZOOM}
        minZoom={5}
        maxZoom={18}
        zoomControl={false}
        scrollWheelZoom={false}
        maxBoundsViscosity={0.85}
        preferCanvas={true}
        className="aida-leaflet-map"
        ref={(map) => {
          if (map) mapRef.current = map;
        }}
      >
        <ZoomControl position="bottomright" />

        <TileLayer
          key={baseMap}
          attribution={activeBaseMap.attribution}
          url={activeBaseMap.url}
        />

        <AlgeriaBoundaryLayer
          algeriaGeoJson={algeriaGeoJson}
          onBoundsReady={(bounds) => {
            algeriaBoundsRef.current = bounds;
          }}
        />

        <SupplierClusterLayer suppliers={suppliers} />
      </MapContainer>
    </div>
  );
};

export default CartographyMap;
